import React, { useMemo } from "react";
import { ADVENT_THEMES, adventParticleCount, adventTheme } from "@/lib/adventThemes";

const CSS = `
.advent-p{position:absolute;display:block;will-change:transform,opacity;animation-timing-function:linear;animation-iteration-count:infinite}
.advent-p-flake,.advent-p-bubble{border-radius:50%}
.advent-p-bubble{background:transparent!important;border:1.5px solid rgba(255,255,255,.7);box-shadow:inset -2px -2px 4px rgba(255,255,255,.35)}
.advent-p-star{clip-path:polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)}
.advent-p-leaf{border-radius:0 100% 0 100%}
.advent-p-petal{border-radius:100% 0 100% 0;transform:rotate(30deg)}
.advent-p-confetti{border-radius:1px}
.advent-p-heart{transform:rotate(-45deg);border-radius:0}
.advent-p-heart::before,.advent-p-heart::after{content:"";position:absolute;width:100%;height:100%;border-radius:50%;background:inherit}
.advent-p-heart::before{top:-50%;left:0}
.advent-p-heart::after{left:50%;top:0}
.advent-p-crescent{border-radius:50%;background:transparent!important;box-shadow:inset -3px 2px 0 0 var(--c)}
.advent-m-fall{top:-12px;animation-name:advent-fall}
.advent-m-sway{top:-14px;animation-name:advent-sway;animation-timing-function:ease-in-out}
.advent-m-rise{bottom:-14px;animation-name:advent-rise}
.advent-m-twinkle{animation-name:advent-twinkle;animation-timing-function:ease-in-out}
.advent-m-float{animation-name:advent-float;animation-timing-function:ease-in-out}
@keyframes advent-fall{to{transform:translateY(115vh) translateX(24px)}}
@keyframes advent-sway{0%{transform:translate3d(0,0,0) rotate(0deg)}33%{transform:translate3d(28px,38vh,0) rotate(120deg)}66%{transform:translate3d(-22px,76vh,0) rotate(240deg)}100%{transform:translate3d(18px,115vh,0) rotate(360deg)}}
@keyframes advent-rise{0%{transform:translateY(0) translateX(0);opacity:0}8%{opacity:1}100%{transform:translateY(-115vh) translateX(-18px);opacity:.15}}
@keyframes advent-twinkle{0%,100%{opacity:.15;transform:scale(.7)}50%{opacity:1;transform:scale(1.15)}}
@keyframes advent-float{0%,100%{transform:translateY(0) rotate(-8deg)}50%{transform:translateY(-18px) rotate(6deg)}}
`;

// Background + themed particles layer for the Advent Calendar card.
export const AdventAmbience = ({ game, count: countProp, className = "" }) => {
  const key = adventTheme(game);
  const theme = ADVENT_THEMES[key];
  const count = countProp ?? adventParticleCount(game);

  const particles = useMemo(() => {
    if (!theme.particle) return [];
    const stationary = theme.motion === "twinkle" || theme.motion === "float";
    return Array.from({ length: count }, (_, i) => {
      const size = theme.particle === "confetti" ? 5 + Math.random() * 4 : 4 + Math.random() * 8;
      return {
        key: i,
        left: Math.random() * 100,
        top: stationary ? Math.random() * 100 : undefined,
        w: size,
        h: theme.particle === "confetti" ? size * 1.8 : theme.particle === "leaf" ? size * 1.5 : size,
        color: theme.colors[Math.floor(Math.random() * theme.colors.length)],
        delay: Math.random() * (stationary ? 4 : 12),
        duration: stationary ? 2.2 + Math.random() * 3.5 : 9 + Math.random() * 10,
        opacity: 0.4 + Math.random() * 0.5,
      };
    });
  }, [count, theme]);

  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
      data-testid={`advent-ambience-${key}`}
    >
      <style>{CSS}</style>
      {particles.map((p) => (
        <span
          key={p.key}
          className={`advent-p advent-p-${theme.particle} advent-m-${theme.motion}`}
          style={{
            left: `${p.left}%`,
            top: p.top != null ? `${p.top}%` : undefined,
            width: p.w,
            height: p.h,
            background: p.color,
            "--c": p.color,
            opacity: p.opacity,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

export default AdventAmbience;
